/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import ChatIcon from '@atlaskit/icon/core/rovo-chat';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';
import { AllAppsIcon } from '@atlassian/search-common/all-apps-icon';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { useProductConfigs } from '../../../../common/constants/products';
import { useAppContext } from '../../../../common/ui/app-context';
import { FlipList } from '../../../../common/ui/flip-list';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import { buildHomeChatUrl } from '../../../../common/utils/quick-find/build-home-chat-url';
import { ChatIntentMode } from '../../../../common/utils/quick-find/chat-intent-mode';
import { getRemainingAppCount } from '../../../../common/utils/quick-find/get-remaining-app-count';
import { AllAppsIcon as AllAppsIconStrip } from '../../../../controllers/all-apps-icon';
import { isUserOAuthed } from '../../../../controllers/oauth';
import { isOpenInNewTabInteraction } from '../../../../controllers/quick-find/is-open-in-new-tab-interaction';
import {
	useAvailableProducts,
	useCloudId,
	useOrgId,
	usePrimarySubProduct,
	useThirdPartyConfigs,
} from '../../../../controllers/store/bootstrap';
import { useQuickFindActions } from '../../../../controllers/store/quick-find';
import { useSearchActions } from '../../../../controllers/store/search';

import { messages } from './messages';
import { useChatIntentKeyboardPreselection } from './use-chat-intent-keyboard-preselection';

/**
 * MOCKED intent detection for M1.
 *
 * If the query contains a `?` anywhere, we treat it as a question and route to chat;
 * otherwise we route to full page search. This is deliberately trivial and lives inline
 * so it is easy to delete when the real ML detector replaces it. Exported for unit tests.
 */
export const detectChatIntentMock = (query: string): ChatIntentMode =>
	query.includes('?') ? ChatIntentMode.CHAT : ChatIntentMode.SEARCH;

const CHAT_ROW_ID = 'intent-chat';
const SEARCH_ROW_ID = 'intent-search';

const CHAT_ROW_TRACKING_ID = `intentRouter:${CHAT_ROW_ID}`;
const SEARCH_ROW_TRACKING_ID = `intentRouter:${SEARCH_ROW_ID}`;

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
	query: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
});

/**
 * Chat intent router rows.
 *
 * Renders two destination rows (Chat / Search) using `SearchResult` so they integrate with
 * `@atlassian/search-dialog` keyboard navigation. The rows are reordered — and the primary
 * row keyboard-preselected — based on (mocked) intent detection of the current query.
 *
 * Rendered behind the `rovo_search_chat_convergence` gate for Confluence and Rovo Home only
 * (see the parent Quick Find dialog). Mutually exclusive with the Jira `IntentRouter`.
 */
export const ChatIntentRouter = ({
	query,
	minQueryLength,
	inputRef,
}: {
	query: string;
	minQueryLength: number;
	inputRef?: React.RefObject<HTMLInputElement>;
}): React.JSX.Element => {
	const intl = useIntl();
	const { generateSearchUrl, queryParams } = useAppContext();
	const [primarySubProduct] = usePrimarySubProduct();
	const [orgId] = useOrgId();
	const [cloudId] = useCloudId();
	const availableProducts = useAvailableProducts();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const productConfigs = useProductConfigs();
	const { setQuickFindOpen } = useQuickFindActions();
	const { clearAllFilters } = useSearchActions();

	const remainingAppCount = useMemo(
		() => getRemainingAppCount(availableProducts, thirdPartyConfigs, productConfigs, isUserOAuthed),
		[availableProducts, thirdPartyConfigs, productConfigs],
	);

	const primaryMode = useMemo<ChatIntentMode>(() => {
		if (query.length < minQueryLength) {
			return ChatIntentMode.SEARCH;
		}
		return detectChatIntentMock(query);
	}, [query, minQueryLength]);

	const searchUrl = useMemo(
		() => generateSearchUrl({ ...queryParams, text: query }),
		[generateSearchUrl, queryParams, query],
	);

	const chatUrl = useMemo(() => {
		if (fg('rovo_search_chat_convergence_chatapp')) {
			return buildHomeChatUrl({ orgId, cloudId, query });
		}
		return generateSearchUrl({ ...queryParams, text: query, mode: 'chat' });
	}, [orgId, cloudId, generateSearchUrl, queryParams, query]);

	const trackingIdByMode = useMemo<Record<ChatIntentMode, string>>(
		() => ({
			[ChatIntentMode.CHAT]: CHAT_ROW_TRACKING_ID,
			[ChatIntentMode.SEARCH]: SEARCH_ROW_TRACKING_ID,
		}),
		[],
	);

	useChatIntentKeyboardPreselection({
		primaryMode,
		query,
		trackingIdByMode,
		inputRef,
	});

	const handleResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			selectedMode: ChatIntentMode,
		) => {
			const isHomeRedirectChatRow =
				selectedMode === ChatIntentMode.CHAT && fg('rovo_search_chat_convergence_chatapp');
			const newTab = isOpenInNewTabInteraction(event) || isHomeRedirectChatRow;
			if (!newTab) {
				setQuickFindOpen(false);
				clearAllFilters();
			}
		},
		[clearAllFilters, setQuickFindOpen],
	);

	const rowsByMode = useMemo<Record<ChatIntentMode, React.JSX.Element>>(
		() => ({
			[ChatIntentMode.CHAT]: (
				<SearchResult
					key={CHAT_ROW_ID}
					id={CHAT_ROW_ID}
					resultType="intentRouter"
					primarySubProduct={primarySubProduct}
					icon={
						<Tile label="" size="small">
							<ChatIcon label={intl.formatMessage(messages.chat)} />
						</Tile>
					}
					title={
						<Flex direction="row" alignItems="center" gap="space.100">
							<Box xcss={styles.query}>{query || intl.formatMessage(messages.searchForQuery)}</Box>
							<Text size="small" color="color.text.subtlest">
								––
							</Text>
							<RovoHexIcon label="" size="xxsmall" />
							<Text size="small" color="color.text.subtlest">
								{intl.formatMessage(messages.chat)}
							</Text>
						</Flex>
					}
					url={chatUrl}
					isThirdParty={fg('rovo_search_chat_convergence_chatapp')}
					onResultClicked={(event) => handleResultClicked(event, ChatIntentMode.CHAT)}
					highlightedRightMetadata={<ReturnIcon />}
				/>
			),
			[ChatIntentMode.SEARCH]: (
				<SearchResult
					key={SEARCH_ROW_ID}
					id={SEARCH_ROW_ID}
					resultType="intentRouter"
					primarySubProduct={primarySubProduct}
					icon={
						<Tile label="" size="small">
							<AiSearchIcon label={intl.formatMessage(messages.search)} />
						</Tile>
					}
					title={
						<Flex direction="row" alignItems="center" gap="space.100">
							<Box xcss={styles.query}>{query || intl.formatMessage(messages.searchForQuery)}</Box>
							<Text size="small" color="color.text.subtlest">
								––
							</Text>
							<AllAppsIcon appCount={remainingAppCount} icon={<AllAppsIconStrip />} />
							<Text size="small" color="color.text.subtlest">
								{intl.formatMessage(messages.search)}
							</Text>
						</Flex>
					}
					url={searchUrl}
					onResultClicked={(event) => handleResultClicked(event, ChatIntentMode.SEARCH)}
					highlightedRightMetadata={<ReturnIcon />}
				/>
			),
		}),
		[query, chatUrl, searchUrl, handleResultClicked, intl, primarySubProduct, remainingAppCount],
	);

	const order = useMemo(
		() =>
			primaryMode === ChatIntentMode.CHAT
				? [CHAT_ROW_ID, SEARCH_ROW_ID]
				: [SEARCH_ROW_ID, CHAT_ROW_ID],
		[primaryMode],
	);

	const renderItem = useCallback(
		(key: string) =>
			key === CHAT_ROW_ID ? rowsByMode[ChatIntentMode.CHAT] : rowsByMode[ChatIntentMode.SEARCH],
		[rowsByMode],
	);

	return (
		<FlipList
			order={order}
			containerAs="div"
			containerProps={{
				role: 'listbox',
				'aria-label': intl.formatMessage(messages.resultsHeading),
			}}
			itemAs="div"
			renderItem={renderItem}
			xcss={styles.root}
		/>
	);
};
